import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import EditableText from "./editable-text";
import MaskedField from "./mask-field";
import EditableSelect from "./editable-select";
import StatusBadge from "./status-badge";
import BatteryBar from "./batttery-bar";
import {
  addElectronicLockbox,
  updateElectronicLockbox,
} from "../../utils/firebasefunctions";
import { toast } from "react-toastify";
import { Check, Pencil, Trash2 } from "lucide-react";
import DeleteRow from "./handle-delete";
import LockboxAssignCell from "./LockboxAssignCell";
import { useStateValue } from "../../context/StateProvider";
import { useNavigate } from "react-router-dom";

const ElectronicTable = forwardRef(
  (
    { rows, setRows, status, onClose, onStatsUpdate, listings, reload, search },
    ref,
  ) => {
    const [editingIds, setEditingIds] = useState(new Set());
    const [IsDeleted, setIsDelete] = useState(false);
    const [IsRowId, setRowId] = useState("");
    const [backupRows, setBackupRows] = useState({});
    const [openStatusId, setOpenStatusId] = useState(null);
    const [loadingId, setLoadingId] = useState(null);
    const navigate = useNavigate();

    const newRowIdsRef = useRef(new Set());
    const rowsRef = useRef(rows);

    useEffect(() => {
      rowsRef.current = rows;
    }, [rows]);

    function update(id, field, value) {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      );
    }

    useImperativeHandle(ref, () => ({ addRow }));

    async function addRow() {
      const tempId = Date.now();
      newRowIdsRef.current.add(tempId);

      const newRow = {
        id: tempId,
        lockboxNumber: "",
        pin: "",
        status: "Available",
        battery: 100,
        notes: "",
        assignedDealLabel: "",
        assignedDealId: "",
      };

      setRows((prev) => [...prev, newRow]);

      setEditingIds((prev) => new Set(prev).add(tempId));
    }

    async function handleDone(id) {
      const row = rowsRef.current.find((r) => r.id === id);
      if (!row) return;

      if (!row.lockboxNumber?.trim()) {
        toast.error("Lockbox # is required");
        return false;
      } else if (!row.pin?.trim()) {
        toast.error("Pin is required");
        return false;
      }

      const { isEditing, ...payload } = row;
      const isNew = newRowIdsRef.current.has(id);

      setLoadingId(id);

      try {
        if (isNew) {
          await addElectronicLockbox({
            ...payload,
            id: String(id),
          });

          newRowIdsRef.current.delete(id);

          setRows((prev) =>
            prev.map((r) =>
              r.id === id
                ? {
                    ...r,
                    id: String(id),
                  }
                : r,
            ),
          );

          toast.success("Row added successfully!");
        } else {
          await updateElectronicLockbox(String(id), payload);
          toast.success("Electronic Lockbox updated successfully!");
        }
        setLoadingId(null);
        if (onStatsUpdate) {
          await onStatsUpdate();
        }
        return true;
      } catch (error) {
        console.error("Firebase error:", error);
        toast.error(error.message || "Kuch problem aayi, dobara try karo.");
      }
    }

    function handleCancel(rowId) {
      if (newRowIdsRef.current.has(rowId)) {
        newRowIdsRef.current.delete(rowId);
        setRows((prev) => prev.filter((r) => r.id !== rowId));
      } else {
        setRows((prev) =>
          prev.map((r) => (r.id === rowId ? backupRows[rowId] : r)),
        );
      }
      setEditingIds((prev) => {
        const copy = new Set(prev);
        copy.delete(rowId);
        return copy;
      });
    }

    const handleDeleteClose = () => {
      setRows((prev) => prev.filter((row) => row.id !== IsRowId));
      onClose();
      setIsDelete(false);
    };

    // Free-text filter: match lockbox #, assigned listing, notes, or status.
    // Rows being edited stay visible regardless so an in-progress add/edit can't
    // vanish out from under the user.
    const q = (search || "").trim().toLowerCase();
    const filteredRows = q
      ? rows.filter(
          (lb) =>
            editingIds.has(lb.id) ||
            [lb.lockboxNumber, lb.assignedDealLabel, lb.notes, lb.status].some(
              (v) => String(v ?? "").toLowerCase().includes(q),
            ),
        )
      : rows;

    return (
      <div>
        <div className="w-full overflow-x-auto h-[500px] rounded-2xl border border-gray-200 shadow-sm bg-white">
          <table className="w-full min-w-[1000px] lg:min-w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left w-[120px] lg:w-[10%] px-4 py-3 text-xs font-semibold uppercase text-gray-500">
                  Lockbox #
                </th>
                <th className="text-center w-[120px] lg:w-[10%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  PIN
                </th>
                <th className="text-center w-[120px] lg:w-[15%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Status
                </th>
                <th className="text-center w-[120px] lg:w-[15%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Battery
                </th>
                <th className="text-center w-[120px] lg:w-[15%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Assigned To
                </th>
                <th className="text-center w-[120px] lg:w-[15%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Notes
                </th>
                <th className="text-center w-[120px] lg:w-[10%] py-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-gray-400 text-sm py-8"
                  >
                    No lockboxes match “{search}”.
                  </td>
                </tr>
              )}
              {filteredRows.map((lb) => {
                const isEditing = editingIds.has(lb.id);
                const isAssigned = lb.status === "Assigned";
                const linksToDeal =
                  isAssigned && !isEditing && lb.assignedDealId;

                return (
                  <tr
                    key={lb.id}
                    onClick={
                      linksToDeal
                        ? () =>
                            navigate(
                              `/dashboard/deals-detail/${lb.assignedDealId}`,
                            )
                        : undefined
                    }
                    className={`border-t border-slate-100 transition-colors ${
                      isAssigned ? "bg-slate-50" : "hover:bg-slate-50/60"
                    } ${linksToDeal ? "cursor-pointer hover:bg-indigo-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <EditableText
                          value={lb.lockboxNumber}
                          onChange={(v) => update(lb.id, "lockboxNumber", v)}
                          mono
                        />
                      ) : (
                        <span className="text-sm font-semibold text-slate-800">
                          {lb.lockboxNumber}
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-center items-center">
                        {isEditing ? (
                          <EditableText
                            value={lb.pin}
                            onChange={(v) => update(lb.id, "pin", v)}
                            mono
                            placeholder="PIN"
                          />
                        ) : (
                          <MaskedField value={lb.pin} />
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-center items-center relative">
                        {isEditing ? (
                          <>
                            <div
                              onClick={() =>
                                setOpenStatusId(
                                  openStatusId === lb.id ? null : lb.id,
                                )
                              }
                              className={`w-full border-2 border-gray-300 rounded-lg p-2 cursor-pointer ${
                                lb.status === ""
                                  ? "text-gray-400"
                                  : "text-textcolor"
                              }`}
                            >
                              {lb.status}
                            </div>

                            <EditableSelect
                              style={{ top: "45px", left: "0px" }}
                              visible={openStatusId === lb.id}
                              activateoutsidecontainerclick={() =>
                                setOpenStatusId(null)
                              }
                              status={status}
                              onClickcorrect={(value) => {
                                update(lb.id, "status", value);
                                setOpenStatusId(null);
                              }}
                            />
                          </>
                        ) : (
                          <StatusBadge status={lb.status} />
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-center items-center">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={lb.battery}
                              onChange={(e) =>
                                update(
                                  lb.id,
                                  "battery",
                                  Math.min(
                                    100,
                                    Math.max(0, Number(e.target.value)),
                                  ),
                                )
                              }
                              className="w-20 border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            />
                            <span className="text-slate-400 text-xs">%</span>
                          </div>
                        ) : (
                          <BatteryBar pct={lb.battery} />
                        )}
                      </div>
                    </td>

                    <td
                      className="text-center px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-center items-center relative">
                        <LockboxAssignCell
                          lb={lb}
                          lockboxType="Electronic"
                          deals={listings}
                          onChanged={reload}
                          disabled={newRowIdsRef.current.has(lb.id)}
                        />
                      </div>
                    </td>

                    <td className="text-center py-3 text-slate-500 text-sm">
                      {isEditing ? (
                        <EditableText
                          value={lb.notes}
                          onChange={(v) => update(lb.id, "notes", v)}
                          placeholder="Notes…"
                        />
                      ) : lb.notes ? (
                        lb.notes
                      ) : (
                        <span className="text-slate-300 line-clamp-1">—</span>
                      )}
                    </td>

                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-center items-center gap-x-2">
                        {isEditing && (
                          <button onClick={() => handleCancel(lb.id)}>
                            ❌
                          </button>
                        )}

                        {!isEditing && !isAssigned && (
                          <button
                            onClick={() => {
                              setIsDelete(true);
                              setRowId(lb.id);
                            }}
                            className="text-sm font-semibold px-2 py-1.5 rounded-md border text-red-600 hover:bg-red-600 hover:text-white"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}

                        {!isEditing && !isAssigned && (
                          <button
                            onClick={() => {
                              setBackupRows((prev) => ({
                                ...prev,
                                [lb.id]: { ...lb },
                              }));

                              setEditingIds((prev) => {
                                const copy = new Set(prev);
                                copy.add(lb.id);
                                return copy;
                              });
                            }}
                            className="text-sm font-semibold px-2 py-1.5 rounded-md border text-indigo-600 hover:bg-indigo-600 hover:text-white"
                          >
                            <Pencil size={18} />
                          </button>
                        )}

                        {!isEditing && isAssigned && (
                          <span className="text-xs font-semibold text-gray-400 italic">
                            Locked
                          </span>
                        )}

                        {isEditing && (
                          <button
                            onClick={async () => {
                              const success = await handleDone(lb.id);

                              if (success) {
                                setEditingIds((prev) => {
                                  const copy = new Set(prev);
                                  copy.delete(lb.id);
                                  return copy;
                                });
                              }
                            }}
                            className="text-sm font-semibold px-2 py-1.5 rounded-md border bg-slate-800 text-white hover:bg-slate-700"
                          >
                            {loadingId === lb.id ? (
                              <svg
                                className="w-5 h-5 text-white animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                ></path>
                              </svg>
                            ) : (
                              <Check size={18} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {/* ✅ ROW END */}
            </tbody>
          </table>
        </div>

        {IsDeleted && (
          <DeleteRow
            rowId={IsRowId}
            onClose={handleDeleteClose}
            onCancel={() => setIsDelete(false)}
          />
        )}
      </div>
    );
  },
);

export default ElectronicTable;
